import { ConvexError } from 'convex/values';
import { AppError } from '@aeon-karaoke/shared/generic/impl/error/AppError';
import { ErrorCode } from '@aeon-karaoke/shared/generic/enum/ErrorCode';
import { Result } from '@aeon-karaoke/shared/generic/impl/util/result';

/**
 * Boundary error wrapping any thrown failure from a one-shot Convex call.
 * Convex functions throw; this is the single place web app code converts those
 * throws into `Result<T, AppError>` (spec §12 — Result lives at the boundary,
 * not inside Convex).
 */
export class ConvexCallError extends AppError {
  readonly code = ErrorCode.InternalError;
  readonly statusCode = 500;

  constructor(message: string) {
    super(message);
  }
}

/**
 * Run a one-shot Convex call (mutation/action) and convert its resolve/throw
 * into `Result`. Reactive reads use `useQuery` directly and stay outside Result.
 * A `ConvexError` carries its reason in `data`, which production keeps while
 * the message is redacted, so the reason wins when it is a string.
 */
export async function toResult<T>(call: () => Promise<T>): Promise<Result<T, ConvexCallError>> {
  try {
    return Result.ok(await call());
  } catch (error) {
    const message =
      error instanceof ConvexError && typeof error.data === 'string'
        ? error.data
        : error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Convex call failed';
    return Result.fail(new ConvexCallError(message));
  }
}
